import { Stack } from '@mui/material';
import { Loader, Modal, SwitchInput } from 'melp-design/components';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import {
  FeatureFlags,
  featureFlagsSchema,
  useFeatureFlags,
  useUpdateFeatureFlags,
} from 'store/feature-flags';
import { ModalProps } from 'store/modal';

type Values = FeatureFlags;

interface Props extends ModalProps {}

export const UpdateFeatureFlagsModal: FC<Props> = ({ closeModal }) => {
  const { t } = useTranslation();

  const { handleSubmit, control, reset } = useForm<Values>({
    defaultValues: featureFlagsSchema.parse({}),
  });

  const { data: featureFlags, isLoading } = useFeatureFlags();

  useEffect(() => {
    if (featureFlags) {
      reset(featureFlags);
    }
  }, [featureFlags, reset]);

  const { mutate: updateFeatureFlags, isLoading: isUpdating } =
    useUpdateFeatureFlags();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateFeatureFlags(
      { flags: values },
      {
        onSuccess: () => {
          closeModal();
        },
      },
    );
  };

  if (isLoading || !featureFlags) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('featureFlags.title')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isUpdating,
          },
        ]}
      >
        <Stack gap={2}>
          {Object.keys(featureFlags).map((key) => (
            <Controller
              key={key}
              name={key as keyof FeatureFlags}
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <SwitchInput
                  label={key}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />
          ))}
        </Stack>
      </Modal.Content>
    </form>
  );
};
